import { createContext, useEffect, useState } from 'react';
import { surveys } from '../utils/surveys';

export const InsightsContext = createContext(null);

function InsightsContextProvider({ children }) {

  const getTop10OccupationsGroups = (surveys) => {
    const groupCounts = surveys.reduce((counts, { occupation }) => {
      if (occupation && occupation.occupational_group && occupation.occupation) {
        const { occupational_group, occupation: occupationName } = occupation;
        if (!counts[occupational_group]) {
          counts[occupational_group] = { totalCount: 0, occupations: {} };
        }
        if (!counts[occupational_group].occupations[occupationName]) {
          counts[occupational_group].occupations[occupationName] = 0;
        }
        counts[occupational_group].occupations[occupationName] += 1;
        counts[occupational_group].totalCount += 1;
      }
      return counts;
    }, {});

    return Object.entries(groupCounts)
      .map(([occupational_group, { totalCount, occupations }]) => ({
        totalcount: totalCount,
        occupational_group,
        occupations
      }))
      .sort((a, b) => b.totalcount - a.totalcount)
      .slice(0, 10);
  };

  const getTop10Occupations = (surveys) => {
    const occupationCounts = surveys.reduce((counts, { occupation }) => {
      if (occupation && occupation.occupation) {
        const { occupation: occupationName } = occupation;
        if (!counts[occupationName]) {
          counts[occupationName] = 0;
        }
        counts[occupationName] += 1;
      }
      return counts;
    }, {});

    return Object.entries(occupationCounts)
      .map(([occupation, count]) => ({ occupation, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  };

  const getTop10IndustryGroups = (surveys) => {
    const groupCounts = surveys.reduce((counts, { industry }) => {
      if (industry && industry.industry_group && industry.industry) {
        const { industry_group: industryGroup, industry: industryName } = industry;
        if (!counts[industryGroup]) {
          counts[industryGroup] = { totalCount: 0, industries: {} };
        }
        if (!counts[industryGroup].industries[industryName]) {
          counts[industryGroup].industries[industryName] = 0;
        }
        counts[industryGroup].industries[industryName] += 1;
        counts[industryGroup].totalCount += 1;
      }
      return counts;
    }, {});

    return Object.entries(groupCounts)
      .map(([industry_group, { totalCount, industries }]) => ({
        totalcount: totalCount,
        industry_group,
        industries
      }))
      .sort((a, b) => b.totalcount - a.totalcount)
      .slice(0, 10);
  };

  const getTop10Industries = (surveys) => {
    const industryCounts = surveys.reduce((counts, { industry }) => {
      if (industry && industry.industry) {
        const { industry: industryName } = industry;
        if (!counts[industryName]) {
          counts[industryName] = 0;
        }
        counts[industryName] += 1;
      }
      return counts;
    }, {});

    return Object.entries(industryCounts)
      .map(([industry, count]) => ({ industry, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  };

  const getTop10SkillGroups = (surveys) => {
    const groupCounts = surveys.reduce((counts, { skill }) => {
      if (skill && skill.skill_group && skill.skill) {
        const { skill_group: skillGroup, skill: skillName } = skill;
        if (!counts[skillGroup]) {
          counts[skillGroup] = { totalCount: 0, skills: {} };
        }
        if (!counts[skillGroup].skills[skillName]) {
          counts[skillGroup].skills[skillName] = 0;
        }
        counts[skillGroup].skills[skillName] += 1;
        counts[skillGroup].totalCount += 1;
      }
      return counts;
    }, {});

    return Object.entries(groupCounts)
      .map(([skill_group, { totalCount, skills }]) => ({
        totalcount: totalCount,
        skill_group,
        skills
      }))
      .sort((a, b) => b.totalcount - a.totalcount)
      .slice(0, 10);
  };

  const getTop10Skills = (surveys) => {
    const skillCounts = surveys.reduce((counts, { skill }) => {
      if (skill && skill.skill) {
        const { skill: skillName } = skill;
        if (!counts[skillName]) {
          counts[skillName] = 0;
        }
        counts[skillName] += 1;
      }
      return counts;
    }, {});

    return Object.entries(skillCounts)
      .map(([skill, count]) => ({ skill, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  };

  const getTop5CauseGroups = (surveys) => {
    const groupCounts = surveys.reduce((counts, { cause }) => {
      if (cause && cause.cause_group && cause.cause) {
        const { cause_group: causeGroup, cause: causeName } = cause;
        if (!counts[causeGroup]) {
          counts[causeGroup] = { totalCount: 0, causes: {} };
        }
        if (!counts[causeGroup].causes[causeName]) {
          counts[causeGroup].causes[causeName] = 0;
        }
        counts[causeGroup].causes[causeName] += 1;
        counts[causeGroup].totalCount += 1;
      }
      return counts;
    }, {});

    return Object.entries(groupCounts)
      .map(([cause_group, { totalCount, causes }]) => ({
        totalcount: totalCount,
        cause_group,
        causes
      }))
      .sort((a, b) => b.totalcount - a.totalcount)
      .slice(0, 5);
  };

  const getTop5Causes = (surveys) => {
    const causeCounts = surveys.reduce((counts, { cause }) => {
      if (cause && cause.cause) {
        const { cause: causeName } = cause;
        if (!counts[causeName]) {
          counts[causeName] = 0;
        }
        counts[causeName] += 1;
      }
      return counts;
    }, {});

    return Object.entries(causeCounts)
      .map(([cause, count]) => ({ cause, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  };
  const getTop5PopulationGroups = (surveys) => {
    const groupCounts = surveys.reduce((counts, { population }) => {
      if (population && population.population_group && population.population) {
        const { population_group: populationGroup, population: populationName } = population;
        if (!counts[populationGroup]) {
          counts[populationGroup] = { totalCount: 0, populations: {} };
        }
        if (!counts[populationGroup].populations[populationName]) {
          counts[populationGroup].populations[populationName] = 0;
        }
        counts[populationGroup].populations[populationName] += 1;
        counts[populationGroup].totalCount += 1;
      }
      return counts;
    }, {});

    return Object.entries(groupCounts)
      .map(([population_group, { totalCount, populations }]) => ({
        totalcount: totalCount,
        population_group,
        populations
      }))
      .sort((a, b) => b.totalcount - a.totalcount)
      .slice(0, 5);
  };

  const getTop5Populations = (surveys) => {
    const populationCounts = surveys.reduce((counts, { population }) => {
      if (population && population.population) {
        const { population: populationName } = population;
        if (!counts[populationName]) {
          counts[populationName] = 0;
        }
        counts[populationName] += 1;
      }
      return counts;
    }, {});

    return Object.entries(populationCounts)
      .map(([population, count]) => ({ population, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  };

  const getTop5Communities = (surveys) => {
    const communityCounts = surveys.reduce((acc, survey) => {
      const community = survey.community;
      if (!acc[community]) {
        acc[community] = 0;
      }
      acc[community] += 1;
      return acc;
    }, {});
  
    const sortedCommunities = Object.entries(communityCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([community, count]) => ({ community, count }));
  
    return sortedCommunities;
  };

  const [top10OccupationalGroups, setTop10OccupationalGroups] = useState([]);
  const [top10IndustryGroups, setTop10IndustryGroups] = useState([]);
  const [top10Occupations, setTop10Occupations] = useState([]);
  const [top10Industries, setTop10Industries] = useState([]);
  const [top10Skills, setTop10Skills] = useState([]);
  const [top10SkillGroups, setTop10SkillGroups] = useState([]);
  const [top5Causes, setTop5Causes] = useState([]);
  const [top5CauseGroups, setTop5CauseGroups] = useState([]);
  const [top5Populations, setTop5Populations] = useState([]);
  const [top5PopulationGroups, setTop5PopulationGroups] = useState([]);
  const [top5Communities, setTop5Communities] = useState([]);


  useEffect(() => {
    if (surveys && surveys.length > 0) {
      setTop10Industries(x => getTop10Industries(surveys));
      setTop10IndustryGroups(x => getTop10IndustryGroups(surveys));
      setTop10Occupations(x => getTop10Occupations(surveys));
      setTop10OccupationalGroups(x => getTop10OccupationsGroups(surveys));
      setTop10SkillGroups(x => getTop10SkillGroups(surveys));
      setTop10Skills(x => getTop10Skills(surveys));
      setTop5CauseGroups(x => getTop5CauseGroups(surveys));
      setTop5Causes(x => getTop5Causes(surveys));
      setTop5PopulationGroups(x => getTop5PopulationGroups(surveys));
      setTop5Populations(x => getTop5Populations(surveys));
      setTop5Communities(x => getTop5Communities(surveys));
    }
  }, [surveys])

  return (
    <InsightsContext.Provider
      value={{
        top10OccupationalGroups,
        top10IndustryGroups,
        top10Occupations,
        top10Industries,
        top10Skills,
        top10SkillGroups,
        top5Causes,
        top5CauseGroups,
        top5Populations,
        top5PopulationGroups,
        top5Communities
      }}
    >
      {children}
    </InsightsContext.Provider>
  );
}

export default InsightsContextProvider;
